import {
  linearRegression,
  linearRegressionThroughOrigin,
  predictY,
  estimateConcentrationFromAbsorbance,
} from '../statistics/regression';
import type { AnalysisOptions, ResultCache } from '../../stores/systems/keisuke/dashboardStore';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import { kt, KeisukeAdapterError } from '../keisukeAdapterI18n';

export function runRegressionAdapter(data: DataRow[], options: AnalysisOptions): ResultCache {
  const mapping = options.mapping ?? {};
  const xCol = mapping.doseColumn ?? mapping.factorAColumn;
  const yCol = mapping.valueColumn;
  if (!xCol || !yCol) {
    throw new KeisukeAdapterError('xyColumnsRequired');
  }

  const points: Array<{ x: number; y: number }> = [];
  for (const row of data) {
    const x = parseFiniteNumberFromCell(row[xCol]);
    const y = parseFiniteNumberFromCell(row[yCol]);
    if (x !== null && y !== null) points.push({ x, y });
  }
  if (points.length < 2) {
    throw new KeisukeAdapterError('regressionMinTwoPoints');
  }

  const regressionType = options.regressionType ?? 'standard';
  const xData = points.map((p) => p.x);
  const yData = points.map((p) => p.y);
  const result =
    regressionType === 'origin'
      ? linearRegressionThroughOrigin(xData, yData)
      : linearRegression(xData, yData);

  const mode = options.regressionMode ?? 'scatter';
  const stats: Record<string, unknown> = {
    slope: result.slope,
    intercept: result.intercept,
    rSquared: result.rSquared,
    mode,
  };

  const observed: [number, number][] = points.map((p) => [p.x, p.y]);
  const xs = points.map((p) => p.x);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const pad = (xMax - xMin) * 0.05 || 1;
  const line: [number, number][] = [];
  const steps = 50;
  for (let i = 0; i <= steps; i++) {
    const x = xMin - pad + (xMax - xMin + 2 * pad) * (i / steps);
    line.push([x, predictY(x, result)]);
  }

  const unknownInput = options.calibrationUnknownSamples ?? [];
  const calibrationEstimates =
    mode === 'calibration' && unknownInput.length > 0
      ? unknownInput.map((sample) => ({
          name: sample.name,
          absorbance: sample.absorbance,
          concentration: estimateConcentrationFromAbsorbance(
            sample.absorbance,
            result,
            regressionType
          ),
        }))
      : undefined;

  const unknownChartPoints: [number, number][] = [];
  if (calibrationEstimates) {
    for (const row of calibrationEstimates) {
      if (row.concentration !== null && Number.isFinite(row.concentration)) {
        unknownChartPoints.push([row.concentration, row.absorbance]);
      }
    }
  }

  const statsDetailLines = [
    kt('statsDetail.regression.equationLine', {
      slope: result.slope.toFixed(4),
      intercept: result.intercept.toFixed(4),
      r2: result.rSquared.toFixed(4),
    }),
  ];
  if (calibrationEstimates?.length) {
    statsDetailLines.push(kt('statsDetail.regression.unknownHeader'));
    for (const row of calibrationEstimates) {
      const conc =
        row.concentration === null ? '—' : row.concentration.toFixed(4);
      statsDetailLines.push(
        kt('statsDetail.regression.unknownLine', {
          name: row.name,
          absorbance: row.absorbance,
          conc,
        })
      );
    }
  }

  return {
    summary: kt(
      mode === 'calibration' ? 'summary.regressionCalibration' : 'summary.regressionLinear',
      { r2: result.rSquared.toFixed(4) }
    ),
    stats,
    statsDetailText: statsDetailLines.join('\n'),
    updatedAt: Date.now(),
    rawResult: { ...result, points, calibrationEstimates },
    calibrationEstimates,
    regressionChart: {
      observed,
      line,
      ...(unknownChartPoints.length > 0 ? { unknown: unknownChartPoints } : {}),
    },
  };
}
